import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardHeader, DataTable, PageError, PageLoading } from "../components/index.js";
import {
  Field, Select, SectionLabel, FieldLabel,
  SOURCE_TYPES, WINDSOR_SOURCE_MAP, SOURCE_CONFIG_FIELD, DEFAULT_TEMPLATE,
  StatusChip,
} from "../components/AdminForm.jsx";
import { KNOWLEDGE_SECTIONS, NOTE_CATEGORIES } from "@prism/shared/knowledge";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { useAuth } from "../context/AuthContext.jsx";
import { timeAgo } from "../lib/time.js";

const TABS = [
  { key: "connections", label: "Connections" },
  { key: "users", label: "Users" },
  { key: "knowledge", label: "Knowledge Base" },
  { key: "settings", label: "Settings" },
  { key: "sync", label: "Sync Log" },
];
const ROLES = [
  { value: "OWNER", label: "Owner" },
  { value: "CLIENT", label: "Client (read-only)" },
  { value: "SALES", label: "Sales" },
];
const GOOGLE_TYPES = new Set(["ga4", "google_search_console"]);
const PROVIDER_OPTIONS = [{ value: "windsor", label: "Windsor.ai" }, { value: "google", label: "Google (direct)" }];

export function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justCreated = searchParams.get("created") === "1";

  const clientApi = useApi(`/api/clients/${id}`);
  // Sync status is best-effort — a failure here degrades to an empty log,
  // exactly like the old `.catch(() => ({ recentLogs: [] }))` fallback.
  const syncApi = useApi(`/api/sync/status?clientId=${id}`);
  const client = clientApi.data;
  const logs = syncApi.data?.recentLogs || [];
  // Initial tab can come from the URL (?tab=knowledge — the cockpit's "Open
  // Knowledge Base" link); unknown values fall back to Connections.
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState(
    TABS.some((t) => t.key === requestedTab) ? requestedTab : "connections"
  );

  // Post-action refresh: silent so the page doesn't flash back to the loading
  // state after every save (matches the old load(), which never re-set loading).
  async function load() {
    await Promise.all([
      clientApi.reload({ silent: true }),
      syncApi.reload({ silent: true }),
    ]);
  }

  if (clientApi.loading || syncApi.loading) return <PageLoading />;
  if (clientApi.error) return <PageError error={clientApi.error} onRetry={() => clientApi.reload()} />;
  if (!client) return null;

  return (
    <div className="space-y-5 max-w-[1100px]">
      {justCreated && (
        <div className="rounded-card border border-accent-border bg-accent-bg px-4 py-3 text-[13px] text-text-primary">
          <span className="font-semibold">Client created.</span> Welcome emails sent and the first sync was triggered.
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 rounded-full border border-border-default flex-shrink-0" style={{ backgroundColor: client.accentColor }} />
          <div>
            <h2 className="text-[18px] font-semibold text-text-primary leading-tight">{client.name}</h2>
            <div className="text-[12px] text-text-muted font-mono">{client.slug}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/clients")}
          className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-[var(--color-border-light)] transition-colors"
        >
          ← All clients
        </button>
      </div>

      <div className="flex gap-1 border-b border-border-default">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-[13px] -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? "border-accent text-text-primary font-semibold"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "connections" && <ConnectionsTab client={client} logs={logs} reload={load} />}
      {tab === "users"       && <UsersTab client={client} reload={load} />}
      {tab === "knowledge"   && <KnowledgeTab client={client} navigate={navigate} />}
      {tab === "settings"    && <SettingsTab client={client} reload={load} />}
      {tab === "sync"        && <SyncLogTab client={client} logs={logs} reload={load} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────── Connections ──

function ConnectionsTab({ client, logs, reload }) {
  const api = useApiAction();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // source being edited
  const [testResult, setTestResult] = useState({}); // sourceId -> result
  const [busyId, setBusyId] = useState(null);

  const { user } = useAuth();
  const superAdmin = user?.role === "SUPER_ADMIN";
  const googleStatus = useApi("/api/admin/google/status", { enabled: superAdmin });
  const [compare, setCompare] = useState(null); // { source, result } | { source, error }
  const [comparing, setComparing] = useState(null);

  async function connectGoogle() {
    try {
      const r = await api("/api/admin/google/connect-url");
      window.location.assign(r.url);
    } catch (e) { alert(e.message); }
  }

  const [syncing, setSyncing] = useState(null);
  async function runSync(r) {
    setSyncing(r.id);
    setTestResult((m) => ({ ...m, [r.id]: null }));
    try {
      const { result } = await api(`/api/clients/${client.id}/sources/${r.id}/sync`, { method: "POST" });
      const ok = !result.error && !result.skipped;
      setTestResult((m) => ({ ...m, [r.id]: {
        success: ok, source: r.type, recordCount: result.records ?? 0, sample: [], window: result.days ? `stored across ${result.days} day${result.days === 1 ? "" : "s"}` : "stored",
        warnings: ok ? (result.records ? [] : ["Sync ran but stored no records for this window."]) : [result.error || result.reason],
      } }));
      await reload();
    } catch (e) {
      setTestResult((m) => ({ ...m, [r.id]: { success: false, source: r.type, recordCount: 0, sample: [], warnings: [e.message] } }));
    } finally {
      setSyncing(null);
    }
  }

  async function runCompare(r) {
    setComparing(r.id); setCompare(null);
    try { setCompare({ source: r, result: await api(`/api/clients/${client.id}/sources/${r.id}/compare`, { method: "POST" }) }); }
    catch (e) { setCompare({ source: r, error: e }); }
    finally { setComparing(null); }
  }

  // Latest sync log per source → records processed.
  const recordsBySource = {};
  for (const l of logs) if (!(l.source in recordsBySource)) recordsBySource[l.source] = l.recordsProcessed;

  async function test(s) {
    setBusyId(s.id);
    setTestResult((m) => ({ ...m, [s.id]: null }));
    try {
      const r = await api(`/api/clients/${client.id}/sources/${s.id}/test`, { method: "POST" });
      setTestResult((m) => ({ ...m, [s.id]: r }));
    } catch (e) {
      setTestResult((m) => ({ ...m, [s.id]: { success: false, warnings: [e.message] } }));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(s) {
    if (!confirm(`Disconnect ${s.type}? Sync history is preserved.`)) return;
    setBusyId(s.id);
    try {
      await api(`/api/clients/${client.id}/sources/${s.id}`, { method: "DELETE" });
      await reload();
    } finally {
      setBusyId(null);
    }
  }

  async function reconnect(r) {
    setBusyId(r.id);
    try {
      await api(`/api/clients/${client.id}/sources/${r.id}`, { method: "PUT", body: JSON.stringify({ status: "connected" }) });
      await reload();
    } catch (e) { alert(e.message); }
    finally { setBusyId(null); }
  }

  const sources = (client.dataSources || []).filter((d) => d.status !== "disconnected");

  return (
    <Card>
      <CardHeader
        title="Data Sources"
        subtitle={`${sources.length} connected`}
        right={
          <button type="button" onClick={() => { setEditing(null); setShowForm((v) => !v); }}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity">
            {showForm ? "Cancel" : "+ Add Data Source"}
          </button>
        }
      />
      {superAdmin && (
        <div className="px-5 py-2 border-b border-border-light text-[12px] flex items-center justify-between gap-3">
          <span className="text-text-secondary">
            Google (direct):{" "}
            {googleStatus.loading ? "checking…"
              : !googleStatus.data ? "unavailable"
              : googleStatus.data.connected ? <span className="text-positive font-medium">Connected as adwords@</span>
              : googleStatus.data.error === "not_configured" ? (googleStatus.data.oauthClient ? "not connected yet" : <>not configured — set <span className="font-mono">GOOGLE_OAUTH_CLIENT_ID</span> / <span className="font-mono">GOOGLE_OAUTH_CLIENT_SECRET</span> in Railway</>)
              : googleStatus.data.error === "reconnect" ? <span className="text-negative">connection revoked — reconnect</span>
              : <span className="text-negative">error: {googleStatus.data.message}</span>}
          </span>
          {googleStatus.data?.oauthClient && !googleStatus.data?.connected && (
            <button type="button" onClick={connectGoogle} className="h-7 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:text-text-primary">Connect Google</button>
          )}
        </div>
      )}
      {(showForm || editing) && (
        <div className="border-b border-border-light px-5 py-4 bg-app-bg">
          <SourceForm
            clientId={client.id}
            source={editing}
            onDone={async () => { setShowForm(false); setEditing(null); await reload(); }}
          />
        </div>
      )}
      {sources.length > 0 ? (
        <DataTable
          columns={[
            { key: "type", label: "Type", render: (r) => <span className="font-medium">{r.type}</span> },
            { key: "provider", label: "Provider / Source", render: (r) => (
                <span className="text-[11px]">
                  <span className="font-medium">{r.type === "metricool" ? "Metricool" : r.provider === "google" ? "Google" : "Windsor"}</span>
                  <span className="font-mono text-text-muted ml-1">{r.type === "metricool" ? "" : r.provider === "google" ? (r.config?.propertyName || r.config?.propertyId || r.config?.siteUrl || "—") : (r.windsorSourceId || "—")}</span>
                </span>
              ) },
            { key: "tmpl", label: "Template / Brand", render: (r) => r.type === "metricool" ? (r.config?.brandName || r.config?.blogId || "—") : (r.config?.fieldTemplate || "—") },
            { key: "status", label: "Status", render: (r) => <StatusChip dataSources={[r]} /> },
            { key: "lastSync", label: "Last Sync", align: "right", render: (r) => timeAgo(r.lastSync) },
            { key: "records", label: "Records", align: "right", render: (r) => recordsBySource[r.type] ?? "—" },
            {
              key: "actions", label: "", align: "right",
              render: (r) => (
                <span className="inline-flex gap-3 justify-end whitespace-nowrap">
                  <button type="button" onClick={() => { setShowForm(false); setEditing(r); }} className="text-[12px] text-accent hover:underline">Edit</button>
                  <button type="button" disabled={busyId === r.id} onClick={() => test(r)} className="text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">Test</button>
                  <button type="button" disabled={syncing === r.id} onClick={() => runSync(r)} className="text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">{syncing === r.id ? "Syncing…" : "Sync"}</button>
                  {GOOGLE_TYPES.has(r.type) && (r.config?.propertyId || r.config?.siteUrl) && (
                    <button type="button" disabled={comparing === r.id} onClick={() => runCompare(r)} className="text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">{comparing === r.id ? "Comparing…" : "Compare"}</button>
                  )}
                  {r.status === "error" && (
                    <button type="button" disabled={busyId === r.id} onClick={() => reconnect(r)} className="text-[12px] text-accent hover:underline disabled:opacity-50">Reconnect</button>
                  )}
                  <button type="button" disabled={busyId === r.id} onClick={() => remove(r)} className="text-[12px] text-text-muted hover:text-negative disabled:opacity-50">Remove</button>
                </span>
              ),
            },
          ]}
          rows={sources}
        />
      ) : (
        <div className="px-5 py-8 text-center text-[13px] text-text-muted">No data sources connected.</div>
      )}
      {Object.entries(testResult).map(([sid, r]) => r && (
        <div key={sid} className="px-5 py-2"><InlineTestResult result={r} /></div>
      ))}
      {compare && (
        <div className="px-5 py-3 border-t border-border-light">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-medium text-text-primary">Google vs stored — {compare.source.type} (last 14 days)</span>
            <button type="button" onClick={() => setCompare(null)} className="text-[12px] text-text-muted hover:text-text-primary">Close</button>
          </div>
          {compare.error ? <div className="text-[12px] text-negative">{compare.error.message}</div> : (
            <>
              <div className="text-[12px] text-text-secondary mb-2">
                {compare.result.summary.daysCompared} days compared · max delta {compare.result.summary.maxAbsDeltaPct == null ? "—" : `${compare.result.summary.maxAbsDeltaPct}%`} on {compare.result.primaryMetric}
                {" "}· {compare.result.summary.daysGoogleOnly} days Google-only · {compare.result.summary.daysStoredZero} days stored at zero
              </div>
              <table className="w-full text-[12px]" style={{ fontFeatureSettings: "'tnum'" }}>
                <thead><tr className="text-left text-text-muted text-[11px] uppercase border-b border-border-light"><th className="py-1 pr-3">Date</th><th className="py-1 pr-3 text-right">Google</th><th className="py-1 pr-3 text-right">Stored</th><th className="py-1 text-right">Delta</th></tr></thead>
                <tbody>
                  {compare.result.days.map((d) => (
                    <tr key={d.date} className="border-b border-border-light">
                      <td className="py-1 pr-3">{d.date}</td>
                      <td className="py-1 pr-3 text-right">{d.google ? d.google[compare.result.primaryMetric] : "—"}</td>
                      <td className="py-1 pr-3 text-right">{d.stored ? d.stored[compare.result.primaryMetric] : "—"}</td>
                      <td className={`py-1 text-right ${d.deltaPct != null && Math.abs(d.deltaPct) > 5 ? "text-negative" : "text-text-secondary"}`}>{d.deltaPct == null ? "—" : `${d.deltaPct > 0 ? "+" : ""}${d.deltaPct}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function SourceForm({ clientId, source, onDone }) {
  const api = useApiAction();
  const editMode = !!source;
  const [type, setType] = useState(source?.type || "ga4");
  const [windsorSourceId, setWindsorSourceId] = useState(source?.windsorSourceId || WINDSOR_SOURCE_MAP.ga4);
  const [config, setConfig] = useState(source?.config || {});
  const [fieldTemplate, setFieldTemplate] = useState(source?.config?.fieldTemplate || DEFAULT_TEMPLATE.ga4 || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const cfgField = SOURCE_CONFIG_FIELD[type];
  const isMetricool = type === "metricool";

  const [provider, setProvider] = useState(source?.provider || "windsor");
  const googleCapable = GOOGLE_TYPES.has(type);
  const isGoogle = googleCapable && provider === "google";
  // The pickers show for every Google-capable type regardless of provider, so a
  // Windsor source can carry the Google property for Compare before the flip (spec §7).
  const propsApi = useApi("/api/admin/google/ga4-properties", { enabled: googleCapable && type === "ga4" });
  const sitesApi = useApi("/api/admin/google/gsc-sites", { enabled: googleCapable && type === "google_search_console" });
  const [filter, setFilter] = useState("");

  // Metricool brands are fetched only when the type is selected — the token
  // check happens server-side; `configured: false` renders a setup notice.
  const brandsApi = useApi("/api/metricool/brands", { enabled: isMetricool });
  const brands = brandsApi.data?.brands || [];

  function changeType(t) {
    setType(t);
    setWindsorSourceId(WINDSOR_SOURCE_MAP[t] || t);
    setFieldTemplate(DEFAULT_TEMPLATE[t] || "");
    setProvider("windsor");
    // A type switch never carries config across types (create mode only —
    // there's no type switch in edit mode): a leftover Metricool blogId/
    // networks (or a Windsor config field) from the previous type must not
    // leak into the new source's config.
    setConfig({});
    setErr(null);
    setFilter("");
  }

  function chooseBrand(blogId) {
    const b = brands.find((x) => x.blogId === blogId);
    if (b?.linkedClientId && b.linkedClientId !== clientId) { setErr(`"${b.name}" is already linked to ${b.linkedClientName}.`); return; }
    setErr(null);
    setConfig(b ? { blogId: b.blogId, brandName: b.name, networks: b.networks } : {});
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      if (isMetricool) {
        if (!config.blogId) throw new Error("Pick a Metricool brand first.");
        const body = { type: "metricool", config };
        if (editMode) await api(`/api/clients/${clientId}/sources/${source.id}`, { method: "PUT", body: JSON.stringify({ config }) });
        else await api(`/api/clients/${clientId}/sources`, { method: "POST", body: JSON.stringify(body) });
      } else if (isGoogle) {
        if (type === "ga4" && !config.propertyId) throw new Error("Pick a GA4 property first.");
        if (type === "google_search_console" && !config.siteUrl) throw new Error("Pick a Search Console site first.");
        const { fieldTemplate: _drop, ...googleConfig } = config;
        const body = { type, provider: "google", config: googleConfig };
        if (editMode) await api(`/api/clients/${clientId}/sources/${source.id}`, { method: "PUT", body: JSON.stringify({ provider: "google", config: googleConfig }) });
        else await api(`/api/clients/${clientId}/sources`, { method: "POST", body: JSON.stringify(body) });
      } else {
        const body = { type, provider: "windsor", windsorSourceId, config: { ...config, fieldTemplate } };
        if (editMode) await api(`/api/clients/${clientId}/sources/${source.id}`, { method: "PUT", body: JSON.stringify({ provider: "windsor", windsorSourceId, config: { ...config, fieldTemplate } }) });
        else await api(`/api/clients/${clientId}/sources`, { method: "POST", body: JSON.stringify(body) });
      }
      await onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const brandOptions = [
    { value: "", label: brandsApi.loading ? "Loading brands…" : "Select a brand" },
    ...brands.map((b) => ({
      value: b.blogId,
      label: b.linkedClientId && b.linkedClientId !== clientId
        ? `${b.name} — linked to ${b.linkedClientName}`
        : `${b.name} [${b.networks.join(", ") || "no networks"}]`,
    })),
  ];

  return (
    <div>
      <SectionLabel>{editMode ? `Edit ${source.type}` : "Add Data Source"}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
        {!editMode && <Select label="Source Type" value={type} onChange={changeType} options={SOURCE_TYPES} />}
        {isMetricool ? (
          brandsApi.error ? (
            <div className="text-[12px] text-negative md:col-span-2">
              Couldn't load Metricool brands: {brandsApi.error.message}
              <button type="button" onClick={() => brandsApi.reload()} className="ml-2 text-accent hover:underline">Retry</button>
            </div>
          ) : brandsApi.data && !brandsApi.data.configured ? (
            <div className="text-[12px] text-text-muted md:col-span-2">
              Metricool isn't configured on the server yet — set <span className="font-mono">METRICOOL_USER_TOKEN</span> and <span className="font-mono">METRICOOL_USER_ID</span>.
            </div>
          ) : (
            <Select label="Metricool Brand" value={config.blogId || ""} onChange={chooseBrand} options={brandOptions} />
          )
        ) : (
          <>
            {googleCapable && <Select label="Provider" value={provider} onChange={setProvider} options={PROVIDER_OPTIONS} />}
            {!isGoogle && (
              <>
                <Field label="Windsor Source ID" mono value={windsorSourceId} onChange={setWindsorSourceId} />
                {cfgField && (
                  <Field label={cfgField.label} placeholder={cfgField.placeholder}
                    value={config?.[cfgField.key] || ""}
                    onChange={(v) => setConfig((c) => ({ ...c, [cfgField.key]: v }))} />
                )}
                <Field label="Field Template" value={fieldTemplate} onChange={setFieldTemplate} />
              </>
            )}
            {googleCapable && type === "ga4" && (
              propsApi.error ? <div className="text-[12px] text-text-muted md:col-span-2">GA4 properties unavailable: {propsApi.error.message}</div> : (
                <>
                  <Field label="Filter properties" value={filter} onChange={setFilter} placeholder="type to filter by account or property" />
                  <Select label="GA4 property (used by the Google provider and Compare)" value={config.propertyId || ""} onChange={(id) => { const p = (propsApi.data?.properties || []).find((x) => x.propertyId === id); setConfig((c) => ({ ...c, propertyId: id, propertyName: p?.displayName, accountName: p?.accountName })); }}
                    options={[{ value: "", label: propsApi.loading ? "Loading properties…" : "Select a property" }, ...(propsApi.data?.properties || []).filter((p) => !filter || `${p.accountName} ${p.displayName} ${p.propertyId}`.toLowerCase().includes(filter.toLowerCase())).map((p) => ({ value: p.propertyId, label: `${p.accountName} — ${p.displayName} (${p.propertyId})` }))]} />
                </>
              )
            )}
            {googleCapable && type === "google_search_console" && (
              sitesApi.error ? <div className="text-[12px] text-text-muted md:col-span-2">Search Console sites unavailable: {sitesApi.error.message}</div> : (
                <>
                  <Field label="Filter sites" value={filter} onChange={setFilter} placeholder="type to filter" />
                  <Select label="Search Console site (used by the Google provider and Compare)" value={config.siteUrl || ""} onChange={(u) => setConfig((c) => ({ ...c, siteUrl: u }))}
                    options={[{ value: "", label: sitesApi.loading ? "Loading sites…" : "Select a site" }, ...(sitesApi.data?.sites || []).filter((s) => !filter || s.siteUrl.toLowerCase().includes(filter.toLowerCase())).map((s) => ({ value: s.siteUrl, label: s.siteUrl }))]} />
                </>
              )
            )}
          </>
        )}
      </div>
      {isMetricool && config.blogId && (
        <div className="text-[12px] text-text-secondary mt-2">
          Networks to sync: {(config.networks || []).join(", ") || "none detected"}
        </div>
      )}
      {err && <div className="text-[12px] text-negative mt-2">{err}</div>}
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={() => onDone()} className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-surface">Cancel</button>
        <button type="button" disabled={busy || (isMetricool && !config.blogId) || (isGoogle && ((type === "ga4" && !config.propertyId) || (type === "google_search_console" && !config.siteUrl)))} onClick={save} className="h-8 px-4 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-60">
          {busy ? "Saving…" : editMode ? "Save" : "Add"}
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────── Users ──

function UsersTab({ client, reload }) {
  const api = useApiAction();
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);

  async function resend(u) {
    setBusyId(u.id);
    setNotice(null);
    try {
      const r = await api(`/api/clients/${client.id}/users/${u.id}/resend-welcome`, { method: "POST" });
      setNotice(r.emailSent ? `Welcome email re-sent to ${u.email}.` : `Welcome link regenerated for ${u.email}.`);
    } catch (e) {
      setNotice(e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function changeRole(u, role) {
    setBusyId(u.id);
    try {
      await api(`/api/clients/${client.id}/users/${u.id}`, { method: "PUT", body: JSON.stringify({ role }) });
      await reload();
    } finally {
      setBusyId(null);
    }
  }

  async function remove(u) {
    if (!confirm(`Remove ${u.email} from this client?`)) return;
    setBusyId(u.id);
    try {
      await api(`/api/clients/${client.id}/users/${u.id}`, { method: "DELETE" });
      await reload();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Users"
        subtitle={`${client.users.length} ${client.users.length === 1 ? "user" : "users"}`}
        right={
          <button type="button" onClick={() => setShowForm((v) => !v)}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity">
            {showForm ? "Cancel" : "+ Invite User"}
          </button>
        }
      />
      {showForm && (
        <div className="border-b border-border-light px-5 py-4 bg-app-bg">
          <InviteUserForm clientId={client.id} onDone={async () => { setShowForm(false); await reload(); }} />
        </div>
      )}
      {notice && <div className="px-5 py-2 text-[12px] text-text-secondary">{notice}</div>}
      <DataTable
        columns={[
          { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
          { key: "email", label: "Email", render: (r) => <span className="text-text-muted">{r.email}</span> },
          {
            key: "role", label: "Role",
            render: (r) => (
              <select
                value={r.role}
                disabled={busyId === r.id}
                onChange={(e) => changeRole(r, e.target.value)}
                className="h-7 px-1.5 rounded border border-border-default text-[12px] bg-surface focus:outline-none focus:border-accent"
              >
                {ROLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ),
          },
          { key: "lastLogin", label: "Last Login", align: "right", render: (r) => (r.lastLogin ? timeAgo(r.lastLogin) : "never") },
          {
            key: "pw", label: "Password",
            render: (r) => r.hasPassword
              ? <span className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#16a34a" }} />Set</span>
              : <span className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#eab308" }} />Pending</span>,
          },
          {
            key: "actions", label: "", align: "right",
            render: (r) => (
              <span className="inline-flex gap-3 justify-end whitespace-nowrap">
                <button type="button" disabled={busyId === r.id} onClick={() => resend(r)} className="text-[12px] text-accent hover:underline disabled:opacity-50">Resend</button>
                <button type="button" disabled={busyId === r.id} onClick={() => remove(r)} className="text-[12px] text-text-muted hover:text-negative disabled:opacity-50">Remove</button>
              </span>
            ),
          },
        ]}
        rows={client.users}
      />
    </Card>
  );
}

function InviteUserForm({ clientId, onDone }) {
  const api = useApiAction();
  const [form, setForm] = useState({ email: "", name: "", role: "SALES" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/clients/${clientId}/users`, { method: "POST", body: JSON.stringify(form) });
      await onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px] gap-x-3 gap-y-2">
        <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
        <Field label="Name" required value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
        <Select label="Role" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} options={ROLES} />
      </div>
      {err && <div className="text-[12px] text-negative mt-2">{err}</div>}
      <div className="flex justify-end mt-3">
        <button type="button" disabled={busy || !form.email || !form.name} onClick={submit}
          className="h-8 px-4 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? "Inviting…" : "Send Invite"}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Settings ──

function SettingsTab({ client, reload }) {
  const api = useApiAction();
  const [form, setForm] = useState({
    name: client.name || "", slug: client.slug || "", website: client.website || "",
    industry: client.industry || "", location: client.location || "",
    accentColor: client.accentColor || "#ff6804", plan: client.plan || "professional",
    windsorApiKey: client.windsorApiKey || "",
  });
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      // The API returns windsorApiKey masked; only send it when the admin typed
      // a new value, so the masked placeholder never overwrites the real key.
      const { windsorApiKey, ...rest } = form;
      const body = windsorApiKey === (client.windsorApiKey || "") ? rest : form;
      await api(`/api/clients/${client.id}`, { method: "PUT", body: JSON.stringify(body) });
      setSaved(true);
      await reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="px-6 py-5">
      <SectionLabel>Client Settings</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 max-w-[720px]">
        <Field label="Business Name" value={form.name} onChange={(v) => upd({ name: v })} />
        <Field label="Slug" mono value={form.slug} onChange={(v) => upd({ slug: v })} />
        <Field label="Website" value={form.website} onChange={(v) => upd({ website: v })} />
        <Field label="Industry" value={form.industry} onChange={(v) => upd({ industry: v })} />
        <Field label="Location" value={form.location} onChange={(v) => upd({ location: v })} />
        <Select label="Plan" value={form.plan} onChange={(v) => upd({ plan: v })}
          options={[{ value: "insights", label: "Prism Insights" }, { value: "professional", label: "Prism Professional" }]} />
        <div>
          <div className="text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>Accent Color</div>
          <div className="flex items-center gap-2">
            <input type="color" value={form.accentColor} onChange={(e) => upd({ accentColor: e.target.value })}
              className="h-9 w-11 rounded border border-border-default cursor-pointer" />
            <input type="text" value={form.accentColor} onChange={(e) => upd({ accentColor: e.target.value })}
              className="flex-1 h-9 px-3 rounded border border-border-default text-[13px] font-mono focus:outline-none focus:border-accent" />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>Windsor API Key</div>
          <div className="flex items-center gap-2">
            <input type={showKey ? "text" : "password"} value={form.windsorApiKey} onChange={(e) => upd({ windsorApiKey: e.target.value })}
              className="flex-1 h-9 px-3 rounded border border-border-default text-[13px] font-mono focus:outline-none focus:border-accent" />
            <button type="button" onClick={() => setShowKey((v) => !v)}
              className="h-9 px-2.5 rounded border border-border-default text-[11px] text-text-secondary hover:bg-[var(--color-border-light)]">
              {showKey ? "Hide" : "Show"}
            </button>
          </div>
        </div>
      </div>
      {err && <div className="text-[12px] text-negative mt-3">{err}</div>}
      <div className="flex items-center gap-3 mt-4">
        <button type="button" disabled={busy} onClick={save}
          className="h-9 px-4 rounded bg-accent text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-60">
          {busy ? "Saving…" : "Save Changes"}
        </button>
        {saved && <span className="text-[12px] text-positive">Saved.</span>}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────── Sync Log ──

function SyncLogTab({ client, logs, reload }) {
  const api = useApiAction();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function triggerSync() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/sync/run?clientId=${client.id}`, { method: "POST" });
      await reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  function duration(l) {
    if (!l.completedAt) return "—";
    const ms = new Date(l.completedAt) - new Date(l.startedAt);
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  }

  const statusColor = (s) => (s === "completed" ? "#16a34a" : s === "failed" ? "#dc2626" : "#eab308");

  return (
    <Card>
      <CardHeader
        title="Sync Log"
        subtitle="Most recent sync events"
        right={
          <button type="button" disabled={busy} onClick={triggerSync}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
            {busy ? "Syncing…" : "Trigger Manual Sync"}
          </button>
        }
      />
      {err && <div className="px-5 py-2 text-[12px] text-negative">{err}</div>}
      {logs.length > 0 ? (
        <DataTable
          columns={[
            { key: "source", label: "Source", render: (r) => <span className="font-medium">{r.source}</span> },
            {
              key: "status", label: "Status",
              render: (r) => (
                <span className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor(r.status) }} />
                  {r.status}
                </span>
              ),
            },
            { key: "recordsProcessed", label: "Records", align: "right", render: (r) => r.recordsProcessed ?? 0 },
            { key: "startedAt", label: "Started", align: "right", render: (r) => timeAgo(r.startedAt) },
            { key: "duration", label: "Duration", align: "right", render: (r) => duration(r) },
            { key: "errorMessage", label: "Error", render: (r) => r.errorMessage ? <span className="text-negative">{r.errorMessage}</span> : "—" },
          ]}
          rows={logs}
        />
      ) : (
        <div className="px-5 py-8 text-center text-[13px] text-text-muted">No sync events yet.</div>
      )}
    </Card>
  );
}

function InlineTestResult({ result }) {
  const ok = result.success && (result.recordCount ?? 0) > 0;
  const window = result.window ?? (result.source === "metricool" ? "for yesterday" : "in the last 7 days");
  return (
    <div className={`text-[12px] rounded px-3 py-2 border ${ok ? "text-positive" : "text-negative"}`}
      style={{ backgroundColor: ok ? "#f0fdf4" : "#fef2f2", borderColor: ok ? "#bbf7d0" : "#fecaca" }}>
      {ok ? `✓ ${result.source}: ${result.recordCount} records ${window}.` : `✗ ${result.source}: ${result.warnings?.[0] || "no data"}`}
    </div>
  );
}

// ──────────────────────────────────────────────────────────── Knowledge ──

// Contract precedence for the supersession banner — mirrors the cockpit's
// currentContractFor: active > sent > latest non-cancelled.
function pickCurrentContract(contracts) {
  const list = (contracts || []).filter((c) => c.status !== "cancelled");
  return (
    list.find((c) => c.status === "active") ||
    list.find((c) => c.status === "sent") ||
    list[list.length - 1] ||
    null
  );
}

function KnowledgeTab({ client, navigate }) {
  const api = useApiAction();
  // Structured contracts supersede the knowledge "contract" stopgap keys —
  // when one exists, the section below shows the real record and marks the
  // legacy keys as historical instead of letting stale values masquerade as
  // current (e.g. a Formidable-era value next to a freshly imported contract).
  const contractsApi = useApi(`/api/contracts?clientId=${client.id}`);
  const currentContract = pickCurrentContract(contractsApi.data?.contracts);
  // Local state (not useApi): saved sections merge the PUT response back into
  // `knowledge`, so the fetch result and mutation results share one store.
  const [knowledge, setKnowledge] = useState(null); // { category: { key: value } }
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api(`/api/clients/${client.id}/knowledge`);
      setKnowledge(r.knowledge || {});
      setNotes(r.notes || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  if (loading) return <Card><div className="px-5 py-8 text-center text-[13px] text-text-muted">Loading knowledge…</div></Card>;
  if (error) return <Card><div className="px-5 py-4 text-[12px] text-negative">{error.message || "Failed to load knowledge."}</div></Card>;

  // Merge the returned knowledge so a saved section's values reflect immediately.
  function mergeKnowledge(updated) { setKnowledge(updated || {}); }

  return (
    <div className="space-y-4">
      {currentContract && (
        <Card className="px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold text-text-primary">
                Contract facts now live in the Contracts system
              </div>
              <div className="text-[12px] text-text-secondary mt-0.5">
                {currentContract.status === "draft" ? "Draft contract" : `Contract (${currentContract.status})`}
                {currentContract.totalMonthlyValue > 0 && ` · $${Number(currentContract.totalMonthlyValue).toLocaleString()}/mo`}
                {currentContract.renewalDate && ` · renews ${new Date(currentContract.renewalDate).toLocaleDateString()}`}
                {" — the Contract section below is the pre-contracts record, kept for reference."}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/agency/contracts")}
              className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-[var(--color-border-light)] whitespace-nowrap"
            >
              Open Contracts
            </button>
          </div>
        </Card>
      )}
      {KNOWLEDGE_SECTIONS.map((section) => (
        <KnowledgeSection
          key={section.key}
          clientId={client.id}
          section={section}
          knowledge={knowledge}
          onSaved={mergeKnowledge}
          superseded={section.key === "contract" && Boolean(currentContract)}
        />
      ))}
      <NotesSection clientId={client.id} notes={notes} onAdded={(n) => setNotes((prev) => [n, ...prev])} />
    </div>
  );
}

function KnowledgeSection({ clientId, section, knowledge, onSaved, superseded = false }) {
  const api = useApiAction();
  const initial = () => {
    const v = {};
    for (const f of section.fields) v[f.key] = knowledge?.[f.category]?.[f.key] ?? "";
    return v;
  };
  // Superseded sections (legacy contract keys shadowed by a structured
  // Contract) start collapsed — reference material, not the live record.
  const [open, setOpen] = useState(!superseded);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  // Re-sync local form if the parent knowledge changes (e.g. after another
  // section saves and the parent re-merges the full record).
  useEffect(() => { setForm(initial()); /* eslint-disable-next-line */ }, [knowledge]);

  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const updates = {};
      for (const f of section.fields) updates[f.key] = form[f.key] ?? "";
      const r = await api(`/api/clients/${clientId}/knowledge`, {
        method: "PUT",
        body: JSON.stringify({ updates }),
      });
      onSaved(r.knowledge);
      setSaved(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={superseded ? "opacity-70" : undefined}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-[13px] font-semibold text-text-primary">
          {section.label}
          {superseded && (
            <span className="ml-2 text-[10px] uppercase font-medium text-text-muted border border-border-default rounded px-1.5 py-0.5" style={{ letterSpacing: "0.5px" }}>
              Superseded by contract
            </span>
          )}
        </span>
        <span className="text-text-muted text-[12px]">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-border-light pt-4">
          {section.fields.map((f) => (
            <div key={f.key}>
              <FieldLabel>{f.label}{f.clientVisible && <span className="ml-2 text-[9px] text-text-muted font-normal lowercase">client-visible</span>}</FieldLabel>
              {f.type === "text" ? (
                <input
                  type="text"
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key)(e.target.value)}
                  className="w-full h-9 px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors"
                />
              ) : (
                <textarea
                  rows={f.type === "json" ? 4 : 3}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key)(e.target.value)}
                  placeholder={f.type === "json" ? '{ "facebook": "https://…" }' : undefined}
                  className={`w-full px-3 py-2 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors resize-y ${f.type === "json" ? "font-mono" : ""}`}
                />
              )}
            </div>
          ))}
          {err && <div className="text-[12px] text-negative">{err}</div>}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save section"}
            </button>
            {saved && <span className="text-[12px] text-positive">Saved.</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

function NotesSection({ clientId, notes, onAdded }) {
  const api = useApiAction();
  const [open, setOpen] = useState(true);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function add() {
    if (!content.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api(`/api/clients/${clientId}/notes`, {
        method: "POST",
        body: JSON.stringify({ content: content.trim(), category }),
      });
      onAdded(r.note);
      setContent("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-[13px] font-semibold text-text-primary">Notes <span className="text-text-muted font-normal">({notes.length})</span></span>
        <span className="text-text-muted text-[12px]">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-border-light pt-4 space-y-4">
          <div className="space-y-2">
            <textarea
              rows={2}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Add a note…"
              className="w-full px-3 py-2 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors resize-y"
            />
            <div className="flex items-center gap-2">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-8 px-2 rounded border border-border-default text-[12px] bg-surface focus:outline-none focus:border-accent"
              >
                {NOTE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                type="button"
                onClick={add}
                disabled={busy || !content.trim()}
                className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {busy ? "Adding…" : "Add note"}
              </button>
              {err && <span className="text-[12px] text-negative">{err}</span>}
            </div>
          </div>
          {notes.length > 0 ? (
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.id} className="rounded border border-border-light px-3 py-2">
                  <div className="text-[13px] text-text-primary whitespace-pre-wrap">{n.content}</div>
                  <div className="mt-1 text-[11px] text-text-muted flex items-center gap-2">
                    {n.category && <span className="uppercase tracking-wide">{n.category}</span>}
                    <span>· {n.author?.name || "system"}</span>
                    <span>· {timeAgo(n.createdAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[12px] text-text-muted">No notes yet.</div>
          )}
        </div>
      )}
    </Card>
  );
}
